import apiService from './api';
import imageUploadService from './imageUpload.service';

export interface Company {
  id: string;
  name: string;
  description?: string;
  imageBase64?: string | null;
  imageUrl?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyDto {
  name: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateCompanyDto {
  name?: string;
  description?: string;
  isActive?: boolean;
}

class CompanyService {
  private baseUrl = '/admin/companies';

  // Get all companies with pagination
  public async getCompanies(page: number = 0, limit: number = 10, search?: string, isActive?: boolean) {
    return apiService.getPaginated<Company>(this.baseUrl, { page, limit, search, isActive });
  }

  // Get all companies (without pagination)
  public async getAllCompanies(): Promise<Company[]> {
    try {
      const response = await apiService.get<any>(`${this.baseUrl}/all`);
      
      // Handle different response formats
      if (response && response.data && Array.isArray(response.data)) {
        // Format: { status: "success", data: [...] }
        return response.data;
      } else if (response && response.status === 'success' && Array.isArray(response.data)) {
        // Format: { status: "success", data: [...] }
        return response.data;
      } else if (Array.isArray(response)) {
        // Direct array response
        return response;
      } else {
        console.error('Unexpected response format from getAllCompanies:', response);
        return [];
      }
    } catch (error) {
      console.error('Error in getAllCompanies:', error);
      return [];
    }
  }

  // Get company by ID
  public async getCompany(id: string) {
    return apiService.get<Company>(`${this.baseUrl}/${id}`);
  }

  // Create company
  public async createCompany(companyData: CreateCompanyDto, image?: File) {
    let finalData: any = { ...companyData };

    // Upload image to ArvanCloud first if provided
    if (image) {
      const uploadResult = await imageUploadService.uploadImage(image, 'products');
      if (uploadResult) {
        finalData.imageUrl = uploadResult.url;
      }
    }

    return apiService.post<Company>(this.baseUrl, finalData);
  }

  // Update company
  public async updateCompany(id: string, companyData: UpdateCompanyDto, image?: File) {
    let finalData: any = { ...companyData };

    // Upload image to ArvanCloud first if provided
    if (image) {
      const uploadResult = await imageUploadService.uploadImage(image, 'products');
      if (uploadResult) {
        finalData.imageUrl = uploadResult.url;
      }
    }

    return apiService.patch<Company>(`${this.baseUrl}/${id}`, finalData);
  }

  // Delete company
  public async deleteCompany(id: string) {
    return apiService.delete<{ success: boolean }>(`${this.baseUrl}/${id}`);
  }

  // Restore company
  public async restoreCompany(id: string) {
    return apiService.post<Company>(`${this.baseUrl}/${id}/restore`);
  }

  // Toggle company status
  public async toggleCompanyStatus(id: string) {
    return apiService.patch<Company>(`${this.baseUrl}/${id}/toggle-status`);
  }

  // Get company image URL (logo)
  public getImageUrl(id: string) {
    return `${process.env.REACT_APP_API_URL}/images/company/${id}`;
  }
}

export default new CompanyService(); 